import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { matchPath } from 'react-router';
import { Link, useLocation } from 'react-router-dom';

import ArrowRightCircleIcon from '@atlaskit/icon/glyph/arrow-right-circle';
import {
    ButtonItem,
    GoBackItem,
    NestableNavigationContent,
    NestingItem,
    Section,
    SideNavigation,
} from '@atlaskit/side-navigation';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsIcon from '@mui/icons-material/Notifications';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TabIcon from '@mui/icons-material/Tab';
import TopicIcon from '@mui/icons-material/Topic';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import Tooltip from 'src/components/common/tooltip';
import { ProposalAccessChecker } from 'src/components/ratecardproposals/ProposalAccessChecker';
import { RatecardProposalProvider } from 'src/components/ratecardproposals/RatecardProposalProvider';
import { RatecardProposalSidebar } from 'src/components/ratecardproposals/RatecardProposalSidebar';
import { RatecardEvaluationSidebar } from 'src/components/ratecards/RatecardEvaluationSidebar';
import { RatecardProjectEditModeSidebar } from 'src/components/ratecards/RatecardProjectEditModeSidebar';
import { RatecardProjectProvider } from 'src/components/ratecards/RatecardProjectProvider';
import RatecardProjectRoute from 'src/components/ratecards/RatecardProjectProviderSuspense';
import { RatecardProjectSidebar } from 'src/components/ratecards/RatecardProjectSidebar';
import { RatecardSuppliersSidebar } from 'src/components/ratecards/RatecardSuppliersSidebar';
import { QuoteEvaluationSidebar } from 'src/components/rfp/QuoteEvaluationSidebar';
import { QuoteProposalProvider } from 'src/components/rfp/QuoteProposalProvider';
import { QuoteProposalSidebar } from 'src/components/rfp/QuoteProposalSidebar';
import { QuoteProvider } from 'src/components/rfp/QuoteProvider';
import { QuoteSidebar } from 'src/components/rfp/QuoteSidebar';
import useAuth from 'src/hooks/useAuth';
import useIsBriefEnabled from 'src/hooks/useIsBriefEnabled';
import useIsUserSupplier from 'src/hooks/useIsUserSupplier';
import CalendarIcon from 'src/icons/Calendar';
import UserIcon from 'src/icons/User';
import UsersIcon from 'src/icons/Users';
import NavigationLink from 'src/links/navigationLink';
import SessionManager from 'src/login/sessionManager';
import useCurrentRoute from 'src/router/useCurrentRoute';
import { RatecardProposalSidebarSkeleton } from 'src/skeletons/ratecardproposals/RatecardProposalSidebarSkeleton';
import { RatecardEvaluationSidebarSkeleton } from 'src/skeletons/ratecards/RatecardEvaluationSidebarSkeleton';
import RatecardProjectEditModeSidebarSkeleton from 'src/skeletons/ratecards/RatecardProjectEditModeSidebarSkeleton';
import { RatecardSidebarSkeleton } from 'src/skeletons/ratecards/RatecardSidebarSkeleton';
import { RatecardSuppliersSidebarSkeleton } from 'src/skeletons/ratecards/RatecardSuppliersSidebarSkeleton';
import { QuoteEvaluationSidebarSkeleton } from 'src/skeletons/rfp/QuoteEvaluationSidebarSkeleton';
import { QuoteProposalSidebarSkeleton } from 'src/skeletons/rfp/QuoteProposalSidebarSkeleton';
import { QuoteSidebarSkeleton } from 'src/skeletons/rfp/QuoteSidebarSkeleton';
import SidebarTutorialModal from 'src/videoModals/SidebarTutorialModal';

export const Root = styled('div')(({ theme }) => ({
    width: '100%',
    height: '100%',
    '& a': {
        textDecoration: 'none !important',
        color: theme.palette.text.primary,
    },
    '& nav': {
        fontFamily: theme.typography.fontFamily,
        '--background-default': theme.palette.background.paper,
        '--text-mediumEmphasis': theme.palette.text.primary,
        '--text-selected': theme.palette.text.primary,
        '--background-transparentNeutral-hover': 'rgba(13, 60, 90, 0.04)',
        '--background-transparentNeutral-pressed': 'rgba(13, 60, 90, 0.04)',
        '--ds-text-subtle': theme.palette.text.primary,
    },
    '& #selectedMenuItem': {
        fontWeight: 'bold',
        color: theme.palette.text.primary,
    },
}));

export const StyledDiv = styled('div')({
    minHeight: '56px',
    textAlign: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
});

export const AtlassianSidebar: FC = () => {
    const { i18n } = useLingui();
    const location = useLocation();
    const { user } = useAuth();
    const currentLocation = useCurrentRoute();
    const isSupplier = useIsUserSupplier();
    const isTenantSet = Boolean(SessionManager.getCurrentTenantId());
    const titleComponent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="button"
                    sx={{
                        fontSize: '11px',
                    }}
                >
                    {i18n._('Apps')}
                </Typography>
                <SidebarTutorialModal />
            </Box>
        ),
        [],
    );
    const { pathname } = location;
    const isBriefEnabled = useIsBriefEnabled();
    const extractPath = (path) => {
        const matchProject = !!matchPath('/dashboard/ratecards/:ratecardId/setup/*', path);
        const matchEditModeInfo = !!matchPath(
            '/dashboard/ratecards/:ratecardId/evaluation/edit/info',
            path,
        );
        const matchEditMode =
            !matchEditModeInfo &&
            !!matchPath('/dashboard/ratecards/:ratecardId/evaluation/edit/*', path);
        const matchSuppliers = !!matchPath(
            '/dashboard/ratecards/:ratecardId/evaluation/suppliers/:proposalId/*',
            path,
        );
        const matchEvaluation =
            !matchSuppliers &&
            !matchEditMode &&
            !!matchPath('/dashboard/ratecards/:ratecardId/evaluation/*', path);
        const matchProposal = !!matchPath(
            '/dashboard/ratecards/:ratecardId/proposals/:proposalId/*',
            path,
        );
        const matchRfpQuote = !!matchPath('/dashboard/rfp/:quoteId/setup/*', path);
        const matchRfpQuoteEvaluation = !!matchPath('/dashboard/rfp/:quoteId/evaluation/*', path);
        const matchRfpQuoteProposal = !!matchPath(
            '/dashboard/rfp/:quoteId/proposals/:quoteProposalId/setup/*',
            path,
        );
        return {
            matchProject,
            matchEvaluation,
            matchProposal,
            matchEditMode,
            matchSuppliers,
            matchRfpQuote,
            matchRfpQuoteEvaluation,
            matchRfpQuoteProposal,
        };
    };

    const isSelected = (path) => !!matchPath(path, pathname);

    const [stack, setStack] = useState<string[]>([]);

    const {
        matchProject,
        matchEvaluation,
        matchProposal,
        matchEditMode,
        matchSuppliers,
        matchRfpQuote,
        matchRfpQuoteEvaluation,
        matchRfpQuoteProposal,
    } = extractPath(pathname);

    useEffect(() => {
        let current =
            matchProject ||
            matchEvaluation ||
            matchProposal ||
            matchRfpQuote ||
            matchRfpQuoteEvaluation ||
            matchRfpQuoteProposal ||
            currentLocation?.sidebar
                ? ['nested']
                : [];

        if (matchEditMode) {
            current = ['nested', 'edit'];
        }

        if (matchSuppliers) {
            current = ['nested', 'suppliers'];
        }

        if (currentLocation?.sidebar) {
            current = ['nested'];
        }

        if (current.join('.') !== stack.join('.')) {
            setStack(current);
        }
    }, [
        matchProject,
        matchEvaluation,
        matchProposal,
        matchRfpQuote,
        matchRfpQuoteEvaluation,
        matchRfpQuoteProposal,
        matchEditMode,
        matchSuppliers,
        currentLocation?.sidebar,
    ]);

    return (
        <Root>
            <SideNavigation label="project" testId="side-navigation">
                <NestableNavigationContent
                    onChange={setStack}
                    stack={stack}
                    testId="nestable-navigation-content"
                    overrides={{
                        GoBackItem: {
                            render: (props) => (
                                <GoBackItem {...props}>{i18n._('Go Back')}</GoBackItem>
                            ),
                        },
                    }}
                >
                    <StyledDiv>
                        {matchProject && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Current Rate Card')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>
                                    <RatecardProjectProvider>
                                        <Suspense fallback={<RatecardSidebarSkeleton />}>
                                            <RatecardProjectSidebar />
                                        </Suspense>
                                    </RatecardProjectProvider>
                                </Section>
                            </NestingItem>
                        )}
                        {matchEvaluation && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Rate Card Evaluation')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>
                                    <RatecardProjectProvider>
                                        <Suspense fallback={<RatecardEvaluationSidebarSkeleton />}>
                                            <RatecardEvaluationSidebar />
                                        </Suspense>
                                    </RatecardProjectProvider>
                                </Section>
                            </NestingItem>
                        )}
                        {matchEditMode && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Rate Card Evaluation')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <NestingItem
                                    id="edit"
                                    title={i18n._('Edit Mode')}
                                    iconBefore={<EditIcon />}
                                    iconAfter={<ArrowRightCircleIcon label="" />}
                                >
                                    <Section>
                                        <RatecardProjectProvider>
                                            <Suspense
                                                fallback={
                                                    <RatecardProjectEditModeSidebarSkeleton />
                                                }
                                            >
                                                <RatecardProjectEditModeSidebar />
                                            </Suspense>
                                        </RatecardProjectProvider>
                                    </Section>
                                </NestingItem>
                                {stack.length === 1 && (
                                    <Section>
                                        <RatecardProjectProvider>
                                            <Suspense
                                                fallback={<RatecardEvaluationSidebarSkeleton />}
                                            >
                                                <RatecardEvaluationSidebar />
                                            </Suspense>
                                        </RatecardProjectProvider>
                                    </Section>
                                )}
                            </NestingItem>
                        )}
                        {matchSuppliers && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Rate Card Evaluation')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <NestingItem
                                    id="suppliers"
                                    title={i18n._('Suppliers')}
                                    iconBefore={<WorkIcon />}
                                    iconAfter={<ArrowRightCircleIcon label="" />}
                                >
                                    <Section>
                                        <RatecardProjectProvider>
                                            <Suspense
                                                fallback={<RatecardSuppliersSidebarSkeleton />}
                                            >
                                                <RatecardSuppliersSidebar />
                                            </Suspense>
                                        </RatecardProjectProvider>
                                    </Section>
                                </NestingItem>
                                {stack.length === 1 && (
                                    <Section>
                                        <RatecardProjectProvider>
                                            <Suspense
                                                fallback={<RatecardEvaluationSidebarSkeleton />}
                                            >
                                                <RatecardEvaluationSidebar />
                                            </Suspense>
                                        </RatecardProjectProvider>
                                    </Section>
                                )}
                            </NestingItem>
                        )}
                        {matchProposal && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Rate Card Proposal')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>
                                    <RatecardProjectRoute>
                                        <ProposalAccessChecker dialog={false}>
                                            <RatecardProposalProvider>
                                                <Suspense
                                                    fallback={<RatecardProposalSidebarSkeleton />}
                                                >
                                                    <RatecardProposalSidebar />
                                                </Suspense>
                                            </RatecardProposalProvider>
                                        </ProposalAccessChecker>
                                    </RatecardProjectRoute>
                                </Section>
                            </NestingItem>
                        )}
                        {matchRfpQuote && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Current Project')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>
                                    <QuoteProvider>
                                        <Suspense fallback={<QuoteSidebarSkeleton />}>
                                            <QuoteSidebar />
                                        </Suspense>
                                    </QuoteProvider>
                                </Section>
                            </NestingItem>
                        )}
                        {matchRfpQuoteEvaluation && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Current Project')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>
                                    <QuoteProvider>
                                        <Suspense fallback={<QuoteEvaluationSidebarSkeleton />}>
                                            <QuoteEvaluationSidebar />
                                        </Suspense>
                                    </QuoteProvider>
                                </Section>
                            </NestingItem>
                        )}
                        {matchRfpQuoteProposal && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Current Project')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>
                                    <QuoteProposalProvider>
                                        <Suspense fallback={<QuoteProposalSidebarSkeleton />}>
                                            <QuoteProposalSidebar />
                                        </Suspense>
                                    </QuoteProposalProvider>
                                </Section>
                            </NestingItem>
                        )}
                        {currentLocation?.sidebar && (
                            <NestingItem
                                id="nested"
                                title={i18n._('Current Project')}
                                iconBefore={<TabIcon />}
                                iconAfter={<ArrowRightCircleIcon label="" />}
                            >
                                <Section>{currentLocation.sidebar}</Section>
                            </NestingItem>
                        )}
                    </StyledDiv>
                    {/* Apps part */}
                    <Section hasSeparator title={titleComponent as unknown as string}>
                        {isBriefEnabled && isTenantSet && (
                            <>
                                <Link to="/dashboard/brief-generation">
                                    <Tooltip
                                        placement="right"
                                        title={i18n._(
                                            'This experimental project brief generation module helps users to create customized project briefs. The modules uses latest AI technologies and a vast database of the apadua ecosystem. The following explains the basic steps how to create project briefs. Please be aware of the disclaimer below and that this module is in development and functionalities are improved regularly. We are happy to receive your feedback via the feedback form or directly to support@apadua.com.',
                                        )}
                                    >
                                        <ButtonItem
                                            iconBefore={<AutoFixHighIcon />}
                                            isSelected={isSelected('/dashboard/brief-generation/*')}
                                            id={
                                                isSelected('/dashboard/brief-generation/*')
                                                    ? 'selectedMenuItem'
                                                    : ''
                                            }
                                            data-testid="projectBriefGenerationModule"
                                        >
                                            {i18n._('Project Brief Generation (beta)')}
                                        </ButtonItem>
                                    </Tooltip>
                                </Link>
                            </>
                        )}
                        {(user.isClient || user.isAgent) && (
                            <>
                                <Link to="/dashboard/rfp">
                                    {user.activeRfps ? (
                                        <Tooltip
                                            title={`${i18n._('You have')} ${
                                                user.activeRfps
                                            } ${i18n._('ongoing project RFPs online')}`}
                                            placement="right"
                                        >
                                            <ButtonItem
                                                iconBefore={<AccountTreeIcon />}
                                                isSelected={isSelected('/dashboard/rfp')}
                                                id={
                                                    isSelected('/dashboard/rfp')
                                                        ? 'selectedMenuItem'
                                                        : ''
                                                }
                                            >
                                                {`${i18n._('Project RFP')} (${user.activeRfps})`}
                                            </ButtonItem>
                                        </Tooltip>
                                    ) : (
                                        <ButtonItem
                                            iconBefore={<AccountTreeIcon />}
                                            isSelected={isSelected('/dashboard/rfp')}
                                            id={
                                                isSelected('/dashboard/rfp')
                                                    ? 'selectedMenuItem'
                                                    : ''
                                            }
                                        >
                                            {i18n._('Project RFP')}
                                        </ButtonItem>
                                    )}
                                </Link>
                                {user.isClient && (
                                    <Link to="/dashboard/ratecards">
                                        {user.activeRfqs ? (
                                            <Tooltip
                                                title={`${i18n._('You have')} ${
                                                    user.activeRfqs
                                                } ${i18n._('ongoing rate card RFQs online')}`}
                                                placement="right"
                                            >
                                                <ButtonItem
                                                    iconBefore={<ReceiptLongIcon />}
                                                    isSelected={isSelected('/dashboard/ratecards')}
                                                    id={
                                                        isSelected('/dashboard/ratecards')
                                                            ? 'selectedMenuItem'
                                                            : ''
                                                    }
                                                >
                                                    {`${i18n._('Rate Card RFQ')} (${
                                                        user.activeRfqs
                                                    })`}
                                                </ButtonItem>
                                            </Tooltip>
                                        ) : (
                                            <ButtonItem
                                                iconBefore={<ReceiptLongIcon />}
                                                isSelected={isSelected('/dashboard/ratecards')}
                                                id={
                                                    isSelected('/dashboard/ratecards')
                                                        ? 'selectedMenuItem'
                                                        : ''
                                                }
                                            >
                                                {i18n._('Rate Card RFQ')}
                                            </ButtonItem>
                                        )}
                                    </Link>
                                )}
                            </>
                        )}
                        {user.isConsultant && (
                            <>
                                <Link to="/dashboard/rfp">
                                    {user.activeRfps ? (
                                        <Tooltip
                                            title={`${i18n._('You have')} ${
                                                user.activeRfps
                                            } ${i18n._('ongoing project RFPs online')}`}
                                            placement="right"
                                        >
                                            <ButtonItem
                                                iconBefore={<AccountTreeIcon />}
                                                isSelected={isSelected('/dashboard/rfp')}
                                                id={
                                                    isSelected('/dashboard/rfp')
                                                        ? 'selectedMenuItem'
                                                        : ''
                                                }
                                            >
                                                {`${i18n._('Project RFP')} (${user.activeRfps})`}
                                            </ButtonItem>
                                        </Tooltip>
                                    ) : (
                                        <ButtonItem
                                            iconBefore={<AccountTreeIcon />}
                                            isSelected={isSelected('/dashboard/rfp')}
                                            id={
                                                isSelected('/dashboard/rfp')
                                                    ? 'selectedMenuItem'
                                                    : ''
                                            }
                                        >
                                            {i18n._('Project RFP')}
                                        </ButtonItem>
                                    )}
                                </Link>
                                <Link to="/dashboard/ratecards">
                                    {user.activeRfqs ? (
                                        <Tooltip
                                            title={`${i18n._('You have')} ${
                                                user.activeRfqs
                                            } ${i18n._('ongoing rate card RFQs online')}`}
                                            placement="right"
                                        >
                                            <ButtonItem
                                                iconBefore={<ReceiptLongIcon />}
                                                isSelected={isSelected('/dashboard/ratecards')}
                                                id={
                                                    isSelected('/dashboard/ratecards')
                                                        ? 'selectedMenuItem'
                                                        : ''
                                                }
                                            >
                                                {`${i18n._('Rate Card RFQ')} (${user.activeRfqs})`}
                                            </ButtonItem>
                                        </Tooltip>
                                    ) : (
                                        <ButtonItem
                                            iconBefore={<ReceiptLongIcon />}
                                            isSelected={isSelected('/dashboard/ratecards')}
                                            id={
                                                isSelected('/dashboard/ratecards')
                                                    ? 'selectedMenuItem'
                                                    : ''
                                            }
                                        >
                                            {i18n._('Rate Card RFQ')}
                                        </ButtonItem>
                                    )}
                                </Link>
                            </>
                        )}
                        {!isSupplier &&
                            (!window.ENABLE_EXPLORE_2 ? (
                                <a href={window.CANONICAL_DOMAIN_EXPLORE}>
                                    <ButtonItem
                                        iconBefore={<TravelExploreIcon />}
                                        isSelected={isSelected('/explore')}
                                        id={isSelected('/explore') ? 'selectedMenuItem' : ''}
                                    >
                                        {i18n._('Explore')}
                                    </ButtonItem>
                                </a>
                            ) : (
                                <Link to="/explore">
                                    <ButtonItem
                                        iconBefore={<TravelExploreIcon />}
                                        isSelected={isSelected('/explore')}
                                        id={isSelected('/explore') ? 'selectedMenuItem' : ''}
                                        data-testid="exploreModule"
                                    >
                                        {i18n._('Explore')}
                                    </ButtonItem>
                                </Link>
                            ))}

                        {window.ENABLE_APALYTICS && !isSupplier ? (
                            <Link to="/apalytics">
                                <ButtonItem
                                    iconBefore={<QueryStatsIcon />}
                                    isSelected={isSelected('/apalytics')}
                                    id={isSelected('/apalytics') ? 'selectedMenuItem' : ''}
                                    data-testid="apalyticsModule"
                                >
                                    <Trans>Apalytics</Trans>
                                </ButtonItem>
                            </Link>
                        ) : null}
                    </Section>
                    {/* General part */}
                    <Section hasSeparator title={i18n._('General')}>
                        <Link to="/dashboard/account/profile">
                            <ButtonItem
                                iconBefore={<UserIcon />}
                                isSelected={isSelected('/dashboard/account/*')}
                                id={isSelected('/dashboard/account/*') ? 'selectedMenuItem' : ''}
                            >
                                {i18n._('Account')}
                            </ButtonItem>
                        </Link>
                        <Link to="/dashboard/calendar">
                            <ButtonItem
                                iconBefore={<CalendarIcon />}
                                isSelected={isSelected('/dashboard/calendar')}
                                id={isSelected('/dashboard/calendar') ? 'selectedMenuItem' : ''}
                            >
                                {i18n._('Calendar')}
                            </ButtonItem>
                        </Link>
                        <Link to="/dashboard/notifications/all">
                            <ButtonItem
                                iconBefore={<NotificationsIcon />}
                                isSelected={isSelected('/dashboard/notifications/all')}
                                id={
                                    isSelected('/dashboard/notifications/all')
                                        ? 'selectedMenuItem'
                                        : ''
                                }
                            >
                                {i18n._('Notifications')}
                            </ButtonItem>
                        </Link>
                    </Section>
                    {/* Organization part */}
                    {user.isUserManager && isTenantSet && (
                        <Section hasSeparator title={i18n._('Organization')}>
                            <Link to="/dashboard/organization/users">
                                <ButtonItem
                                    iconBefore={<UsersIcon />}
                                    isSelected={isSelected('/dashboard/organization/users')}
                                    id={
                                        isSelected('/dashboard/organization/users')
                                            ? 'selectedMenuItem'
                                            : ''
                                    }
                                >
                                    {i18n._('Users')}
                                </ButtonItem>
                            </Link>
                        </Section>
                    )}
                    {/* Settings part */}
                    {user.isClient &&
                        isTenantSet &&
                        (user.apaduaRole === 'user' || user.apaduaRole === 'admin') && (
                            <Section hasSeparator title={i18n._('Settings')}>
                                <Box data-testid="settingsNavigation">
                                    <NavigationLink
                                        href={`${window.CANONICAL_DOMAIN_SOURCE}#/app/templates`}
                                    >
                                        <ButtonItem iconBefore={<TopicIcon />}>
                                            {i18n._('Evaluation templates')}
                                        </ButtonItem>
                                    </NavigationLink>
                                    <NavigationLink
                                        href={`${window.CANONICAL_DOMAIN_SOURCE}#/app/org-settings`}
                                    >
                                        <ButtonItem iconBefore={<BusinessIcon />}>
                                            {i18n._('Organization settings')}
                                        </ButtonItem>
                                    </NavigationLink>
                                </Box>
                            </Section>
                        )}
                </NestableNavigationContent>
            </SideNavigation>
        </Root>
    );
};
